import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Bot, User, ShieldCheck, LifeBuoy, CalendarClock, Sparkles, Wrench, Volume2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { InlineCard } from "./InlineCard";

function Markdownish({ text }) {
  // Lightweight rendering: code fences + inline code + bold + line breaks
  const blocks = text.split(/```/);
  return (
    <div className="space-y-2 text-[15px] leading-relaxed">
      {blocks.map((block, i) => {
        if (i % 2 === 1) {
          const lines = block.split("\n");
          const body = lines[0].match(/^[a-zA-Z]+$/) ? lines.slice(1).join("\n") : block;
          return (
            <pre key={i} className="rounded-xl bg-[#0A0E14] border border-[var(--border)] p-3 overflow-x-auto">
              <code className="font-mono-tech text-[13px] text-[var(--tech-accent)] whitespace-pre">{body.trim()}</code>
            </pre>
          );
        }
        return (
          <div key={i}>
            {block.split("\n").map((line, j) => (
              <p key={j} className={line.trim() === "" ? "h-2" : ""}
                 dangerouslySetInnerHTML={{
                   __html: line
                     .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
                     .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
                     .replace(/`(.+?)`/g, '<code class="font-mono-tech text-[13px] text-[var(--primary)] bg-black/40 px-1.5 py-0.5 rounded">$1</code>')
                     .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-[var(--primary)] underline decoration-dotted underline-offset-2 hover:decoration-solid break-all">$1</a>'),
                 }}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

const EMPTY_TILES = [
  { icon: ShieldCheck, label: "Warranty claims", color: "#FF3B30" },
  { icon: LifeBuoy, label: "Post-sales support", color: "#FF6B00" },
  { icon: CalendarClock, label: "Book appointments", color: "#FFB000" },
  { icon: Sparkles, label: "Sales enquiries", color: "#FF8A33" },
];

function EmptyState({ mode }) {
  const isTech = mode === "technician";
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6 py-10">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className={`h-16 w-16 rounded-2xl grid place-items-center mb-5 ${isTech ? "bg-[var(--tech-accent)]/15" : "bg-[var(--primary)]/15"}`}
      >
        {isTech ? <Wrench size={28} className="text-[var(--tech-accent)]" /> : <Bot size={28} className="text-[var(--primary)]" />}
      </motion.div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
        {isTech ? "Tech Co-Pilot" : "How can I help you today?"}
      </h1>
      <p className="mt-3 max-w-md text-[var(--text-secondary)]">
        {isTech
          ? "Deep technical troubleshooting, diagnostic playbooks and runnable CLI commands to help your techs resolve issues fast."
          : "Apex, your it IN A bit AI assistant for warranty issues, post-sales support, appointment bookings and sales enquiries."}
      </p>
      {!isTech && (
        <div className="mt-8 grid grid-cols-2 gap-3 max-w-md w-full">
          {EMPTY_TILES.map((t) => (
            <div key={t.label} className="flex items-center gap-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface)]/60 px-3.5 py-3">
              <t.icon size={16} style={{ color: t.color }} />
              <span className="text-sm text-[var(--text-secondary)]">{t.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function MessageList({ messages, streaming, mode, onSpeak, onPickSlot }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  if (messages.length === 0 && !streaming) return <EmptyState mode={mode} />;

  return (
    <ScrollArea className="flex-1 min-w-0 [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:!min-w-0">
      <div className="mx-auto w-full max-w-3xl px-4 md:px-6 py-6 space-y-6" data-testid="message-list">
        {messages.map((m, idx) => {
          const isUser = m.role === "user";
          return (
            <motion.div
              key={m.id || idx}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}
              data-testid={`message-${m.role}`}
            >
              <div className={`h-8 w-8 rounded-lg grid place-items-center shrink-0 ${isUser ? "bg-white/10" : mode === "technician" ? "bg-[var(--tech-accent)]" : "bg-[var(--primary)]"}`}>
                {isUser ? <User size={15} className="text-[var(--text-secondary)]" /> : <Bot size={15} className="text-black" />}
              </div>
              <div className={`min-w-0 max-w-[85%] ${isUser ? "items-end" : ""}`}>
                {m.images && m.images.length > 0 && (
                  <div className={`flex flex-wrap gap-2 mb-2 ${isUser ? "justify-end" : ""}`} data-testid="message-images">
                    {m.images.map((src, i) => (
                      <img key={i} src={src} alt="attachment" className="h-28 w-28 object-cover rounded-xl border border-[var(--border)]" data-testid="message-image" />
                    ))}
                  </div>
                )}
                {(m.content || !isUser) && (
                  <div className={`rounded-2xl px-4 py-3 min-w-0 [overflow-wrap:anywhere] ${isUser ? "bg-white/8 text-[var(--text-primary)]" : "bg-[var(--surface)] border border-[var(--border)]"}`}>
                    {m.content ? <Markdownish text={m.content} /> : (
                      <span className="inline-flex gap-1 items-center text-[var(--text-muted)]">
                        <span className="h-2 w-2 rounded-full bg-current pulse-dot" />
                        <span className="h-2 w-2 rounded-full bg-current pulse-dot" style={{ animationDelay: "0.2s" }} />
                        <span className="h-2 w-2 rounded-full bg-current pulse-dot" style={{ animationDelay: "0.4s" }} />
                      </span>
                    )}
                    {m.streaming && m.content && <span className="cursor-blink text-[var(--primary)] font-bold">▌</span>}
                  </div>
                )}
                {!isUser && !m.streaming && m.content && onSpeak && (
                  <button
                    data-testid="message-speak-button"
                    onClick={() => onSpeak(m.content)}
                    title="Play voice"
                    className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-mono-tech text-[var(--text-muted)] transition-colors duration-200 hover:text-[var(--primary)]"
                  >
                    <Volume2 size={13} /> Play
                  </button>
                )}
                {m.card && <InlineCard card={m.card} onPickSlot={streaming ? null : onPickSlot} />}
              </div>
            </motion.div>
          );
        })}
        <div ref={endRef} />
      </div>
    </ScrollArea>
  );
}
